import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { Wallet, Euro, Trash2, RefreshCw, CalendarDays } from 'lucide-react';

// ★ 2026-08-11 (Fase 37) — Card "Pagamenti registrati" del lead
// Elenca i proposal_payments collegati al lead con la possibilità di
// eliminarli inline (solo admin CEO / Eufemia / Morena / Claudia).

const fmtEur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
    return d.toLocaleDateString('it-IT');
  } catch { return iso; }
};

export function LeadPaymentsCard({ leadId, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [total, setTotal] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/payments', { params: { lead_id: leadId, limit: 100 } });
      setItems(data?.items || []);
      setIsAdmin(!!data?.is_admin);
      setTotal(data?.total_amount_eur || 0);
    } catch (err) {
      // silenzioso — potrebbe essere che l'utente non abbia visibilità
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/payments/${deleteTarget.id}`);
      toast.success(`Pagamento ${fmtEur(deleteTarget.amount_eur)} eliminato`);
      setDeleteTarget(null);
      await load();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione pagamento');
    } finally {
      setDeleting(false);
    }
  };

  // Non mostrare la card se non ci sono pagamenti e non ho ancora caricato
  if (loading) {
    return (
      <Card data-testid="lead-payments-card">
        <CardContent className="p-4 space-y-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) return null;

  return (
    <Card className="border-emerald-200/60" data-testid="lead-payments-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-700" />
            <h3 className="font-semibold text-sm">Pagamenti registrati</h3>
            <Badge variant="outline" className="text-[10px]">
              {items.length} {items.length === 1 ? 'incasso' : 'incassi'}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm">
              <span className="text-xs text-muted-foreground mr-1">Totale:</span>
              <span className="font-serif font-semibold text-emerald-700">{fmtEur(total)}</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={load}
              data-testid="lead-payments-refresh"
              aria-label="Aggiorna pagamenti"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {items.map((p) => (
            <div
              key={p.id}
              className="flex items-start justify-between gap-3 p-2.5 rounded border border-border bg-[var(--surface-1)] hover:border-emerald-300 transition-colors"
              data-testid={`lead-payment-row-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif font-semibold text-emerald-700">
                    <Euro className="w-3.5 h-3.5 inline mr-0.5" />
                    {Number(p.amount_eur || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    {p.proposal_snapshot?.name || 'Proposta'}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" />
                    {fmtDate(p.paid_at)}
                  </span>
                  {p.payment_method && (
                    <span className="italic max-w-[280px] truncate" title={p.payment_method}>
                      {p.payment_method}
                    </span>
                  )}
                  {p.closed_by?.name && (
                    <span>· Chiuso da <b>{p.closed_by.name}</b></span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-8 shrink-0"
                  onClick={() => setDeleteTarget(p)}
                  data-testid={`lead-payment-delete-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Elimina
                </Button>
              )}
            </div>
          ))}
        </div>

        {!isAdmin && (
          <div className="text-[11px] text-muted-foreground mt-3 italic">
            Solo gli admin CEO possono eliminare pagamenti registrati.
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="lead-payment-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Eliminare questo pagamento?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {deleteTarget && (
                  <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Importo</span>
                      <span className="font-serif text-lg font-semibold text-red-700">
                        {fmtEur(deleteTarget.amount_eur)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Proposta</span>
                      <span className="font-medium">{deleteTarget.proposal_snapshot?.name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Data</span>
                      <span>{fmtDate(deleteTarget.paid_at)}</span>
                    </div>
                    {deleteTarget.closed_by?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase text-muted-foreground">Chiuso da</span>
                        <span>{deleteTarget.closed_by.name}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="text-red-600 font-medium text-sm">
                  L&apos;operazione è irreversibile. Il pagamento verrà rimosso dai KPI e dalla Dashboard Incassi.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="lead-payment-delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? 'Eliminazione…' : 'Sì, elimina pagamento'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default LeadPaymentsCard;
